import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { groupBuyApi, categoryApi } from '../api';
import { GroupBuy, GroupBuyPhase } from '../types';
import GroupBuyCard from '../components/groupbuy/GroupBuyCard';
import Loading from '../components/common/Loading';

const PHASE_FILTERS: { label: string; value: GroupBuyPhase }[] = [
  { label: 'Upcoming', value: 'UPCOMING' },
  { label: 'Collecting', value: 'COLLECTING' },
  { label: 'Closed', value: 'CLOSED' },
];

const VALID_PHASES: GroupBuyPhase[] = ['UPCOMING', 'COLLECTING', 'CLOSED'];

const GroupBuyListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<GroupBuy[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [phaseCatIds, setPhaseCatIds] = useState<Set<number>>(new Set());
  const [categoryId, setCategoryId] = useState<number | null>(null);

  const phaseParam = searchParams.get('phase') as GroupBuyPhase | null;
  const phase: GroupBuyPhase = phaseParam && VALID_PHASES.includes(phaseParam) ? phaseParam : 'COLLECTING';

  useEffect(() => {
    categoryApi.getAll().then(r => {
      const flat: { id: number; name: string }[] = [];
      const walk = (cats: any[]) => cats.forEach(c => { flat.push({ id: c.id, name: c.name }); if (c.children) walk(c.children); });
      walk(r.data.data as any[]);
      setCategories(flat);
    }).catch(() => {});
  }, []);

  // 현재 phase에 실제로 존재하는 한정판매의 카테고리만 추려 필터 바에 노출
  useEffect(() => {
    groupBuyApi.getList(phase, 0, null, 100)
      .then(r => {
        const ids = new Set<number>();
        r.data.data.content.forEach(g => { if (g.categoryId != null) ids.add(g.categoryId); });
        setPhaseCatIds(ids);
      })
      .catch(() => setPhaseCatIds(new Set()));
  }, [phase]);

  useEffect(() => {
    setLoading(true);
    groupBuyApi.getList(phase, page, categoryId)
      .then(r => {
        setItems(r.data.data.content);
        setTotalPages(r.data.data.totalPages);
      })
      .finally(() => setLoading(false));
  }, [phase, page, categoryId]);

  const handleFilter = (p: GroupBuyPhase) => {
    setSearchParams({ phase: p });
    setPage(0);
    setCategoryId(null);
  };

  const shownCategories = categories.filter(c => phaseCatIds.has(c.id));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Limited-Time Drops</h1>
      </div>

      <div style={styles.filters}>
        {PHASE_FILTERS.map(f => (
          <button
            key={f.label}
            style={{ ...styles.filterBtn, ...(phase === f.value ? styles.filterBtnActive : {}) }}
            onClick={() => handleFilter(f.value)}
          >{f.label}</button>
        ))}
      </div>

      {shownCategories.length > 0 && (
        <div style={styles.filters}>
          <button
            style={{ ...styles.filterBtn, ...(categoryId === null ? styles.filterBtnActive : {}) }}
            onClick={() => { setCategoryId(null); setPage(0); }}
          >All</button>
          {shownCategories.map(c => (
            <button
              key={c.id}
              style={{ ...styles.filterBtn, ...(categoryId === c.id ? styles.filterBtnActive : {}) }}
              onClick={() => { setCategoryId(c.id); setPage(0); }}
            >{c.name}</button>
          ))}
        </div>
      )}

      {loading ? <Loading /> : items.length === 0 ? (
        <div style={styles.empty}>
          <p>No group buys available.</p>
        </div>
      ) : (
        <div style={styles.grid}>
          {items.map(gb => <GroupBuyCard key={gb.id} groupBuy={gb} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              style={{ ...styles.pageBtn, ...(page === i ? styles.pageBtnActive : {}) }}
              onClick={() => setPage(i)}
            >{i + 1}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '0 auto', padding: '40px 20px' },
  header: { textAlign: 'center', marginBottom: 40 },
  title: { fontSize: 32, fontWeight: 800, color: '#333', marginBottom: 8 },
  filters: { display: 'flex', gap: 8, marginBottom: 32, flexWrap: 'wrap' },
  filterBtn: { padding: '8px 18px', border: '1px solid #e8e8e8', borderRadius: 20, background: '#fff', fontSize: 13, color: '#666', cursor: 'pointer', fontWeight: 500 },
  filterBtnActive: { background: '#b39ddb', color: '#fff', borderColor: '#b39ddb', fontWeight: 700 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 24 },
  empty: { textAlign: 'center', padding: '80px 0', color: '#bbb', fontSize: 16 },
  pagination: { display: 'flex', justifyContent: 'center', gap: 6, marginTop: 40 },
  pageBtn: { width: 36, height: 36, border: '1px solid #e8e8e8', background: '#fff', borderRadius: 8, fontSize: 13, color: '#555', cursor: 'pointer' },
  pageBtnActive: { background: '#b39ddb', color: '#fff', border: 'none' },
};

export default GroupBuyListPage;
